import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PublicClientApplication, Configuration } from '@azure/msal-browser';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import CalendarDashboard from './components/CalendarDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationProvider from './components/NotificationProvider';
import LoginScreen from './components/LoginScreen';
import { createMsalConfig } from './config/msalConfig';
import { registerMsalInstance } from './services/authToken';
import { THEME_COLORS } from './constants';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: THEME_COLORS.PRIMARY,
    },
    secondary: {
      main: THEME_COLORS.SECONDARY,
    },
    error: {
      main: THEME_COLORS.ERROR,
    },
    warning: {
      main: THEME_COLORS.WARNING,
    },
    info: {
      main: THEME_COLORS.INFO,
    },
    success: {
      main: THEME_COLORS.SUCCESS,
    },
    background: {
      default: THEME_COLORS.BACKGROUND,
      paper: THEME_COLORS.SURFACE,
    },
    text: {
      primary: THEME_COLORS.TEXT_PRIMARY,
      secondary: THEME_COLORS.TEXT_SECONDARY,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const AppContent: React.FC = () => {
  const { instance } = useMsal();

  useEffect(() => {
    // Set active account on page load if we have accounts but no active account
    const allAccounts = instance.getAllAccounts();
    if (allAccounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(allAccounts[0]);
      console.log('Set active account from existing accounts');
    }
  }, [instance]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <AuthenticatedTemplate>
        <CalendarDashboard />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
    </Box>
  );
};

const MsalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        // Load dynamic configuration first
        const dynamicConfig = await createMsalConfig();

        // Create MSAL instance with dynamic config
        const instance = new PublicClientApplication(dynamicConfig);

        // Initialize the instance
        await instance.initialize();

        // Make the instance available to the API layer so requests to our
        // backend can carry the signed-in user's token.
        registerMsalInstance(instance);

        setMsalInstance(instance);
        console.log('MSAL initialized with dynamic config, clientId:', dynamicConfig.auth.clientId);
      } catch (error) {
        console.error('Failed to initialize MSAL with dynamic config:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeMsal();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!msalInstance) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <div>Failed to initialize authentication. Please refresh the page.</div>
      </Box>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      {children}
    </MsalProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <NotificationProvider>
            <MsalWrapper>
              <AppContent />
            </MsalWrapper>
          </NotificationProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
